import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Circle, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// React-leaflet's default marker icons rely on bundler-discovered URLs that
// CRA mis-resolves. Wire the marker to the CDN so we don't have to copy assets.
const defaultIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

interface Props {
  /** Center of the geofence (property location). */
  lat: number | null;
  lng: number | null;
  /** Radius in meters. */
  radius: number;
  onChange: (next: { lat: number; lng: number; radius: number }) => void;
  height?: number;
}

const ClickToSet: React.FC<{ onClick: (latlng: L.LatLng) => void }> = ({ onClick }) => {
  useMapEvents({
    click(e) { onClick(e.latlng); },
  });
  return null;
};

export const GeofencePicker: React.FC<Props> = ({ lat, lng, radius, onChange, height = 360 }) => {
  // Default to a sensible US center until the PM picks a location.
  const center = useMemo<[number, number]>(() => (
    lat != null && lng != null ? [lat, lng] : [38.9, -77.03]
  ), [lat, lng]);
  const [localRadius, setLocalRadius] = useState(radius);
  useEffect(() => { setLocalRadius(radius); }, [radius]);

  const markerRef = useRef<L.Marker | null>(null);
  const handleMarkerDrag = () => {
    const m = markerRef.current;
    if (!m) return;
    const ll = m.getLatLng();
    onChange({ lat: ll.lat, lng: ll.lng, radius: localRadius });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation not supported in this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude, radius: localRadius }),
      (err) => alert(`Location request failed: ${err.message}`),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button
          type="button"
          onClick={useMyLocation}
          className="px-2.5 py-1 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          📍 Use my current location
        </button>
        <span className="text-gray-500">— or click the map / drag the marker</span>
      </div>

      <div style={{ height }} className="rounded-lg overflow-hidden border border-gray-200">
        <MapContainer
          center={center}
          zoom={lat != null && lng != null ? 17 : 5}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.esri.com">Esri</a>, Maxar, Earthstar Geographics'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
          />
          <ClickToSet onClick={(latlng) => onChange({ lat: latlng.lat, lng: latlng.lng, radius: localRadius })} />
          {lat != null && lng != null && (
            <>
              <Marker
                position={[lat, lng]}
                draggable
                icon={defaultIcon}
                eventHandlers={{ dragend: handleMarkerDrag }}
                ref={(ref) => { if (ref) markerRef.current = ref; }}
              />
              <Circle
                center={[lat, lng]}
                radius={localRadius}
                pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.15 }}
              />
            </>
          )}
        </MapContainer>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Radius (m):</label>
        <input
          type="range"
          min={20}
          max={1000}
          step={10}
          value={localRadius}
          onChange={(e) => {
            const r = Number(e.target.value);
            setLocalRadius(r);
            if (lat != null && lng != null) onChange({ lat, lng, radius: r });
          }}
          className="flex-1"
        />
        <input
          type="number"
          min={20}
          max={2000}
          value={localRadius}
          onChange={(e) => {
            const r = Number(e.target.value);
            setLocalRadius(r);
            if (lat != null && lng != null) onChange({ lat, lng, radius: r });
          }}
          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
        />
      </div>
      <p className="text-[11px] text-gray-500">
        Typical radius: <strong>50–200 m</strong> for a single building, up to <strong>500 m</strong> for a campus.
      </p>
    </div>
  );
};
